import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import '../App.css';
import {
    Button, Modal, Tag, Typography, Card, Table, Space,
    InputNumber, Spin, Empty, Image, Form, Input, Select, AutoComplete, Tabs,
} from 'antd';
import { PlusOutlined, EditOutlined, ToolOutlined, LinkOutlined } from '@ant-design/icons';
import PageLayout from '../components/PageLayout';
import { useSnackbar } from 'notistack';
import {
    getPropertyConfig, getPropertyList,
    getCostVariables, addCostVariable, updateCostVariable,
    getVariableGroups, logoutUser,
} from '../apiCalls.js';
import { useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { getCurrencySymbol } from '../utils/currencyUtils';
import { useCleanupRequests } from '../hooks/useCleanupRequests';
import { useOptimisticUpdate } from '../hooks/useOptimisticUpdate';
import { useUser } from '../hooks/useUser';
import { isSuperAdmin } from '../utils/roleUtils';

const { Title, Text } = Typography;

const toCamelCase = (str) =>
    str.trim().split(/[\s\-_]+/).filter(Boolean).map((word, i) => {
        const clean = word.replace(/[^a-zA-Z0-9]/g, '');
        if (!clean) return '';
        return i === 0
            ? clean.charAt(0).toLowerCase() + clean.slice(1)
            : clean.charAt(0).toUpperCase() + clean.slice(1);
    }).join('');

const formatTypeLabel = (type) => {
    const safeType = typeof type === 'string' ? type : '';
    if (!safeType) return 'Unknown';
    return safeType.charAt(0).toUpperCase() + safeType.slice(1);
};

function Home() {
    const { t } = useTranslation();
    const { user } = useUser();
    const { savingIds, executeOptimistic } = useOptimisticUpdate();
    useCleanupRequests();

    const [loading, setLoading] = useState(false);
    const [fetchLoading, setFetchLoading] = useState(false);
    const [propertyConfig, setPropertyConfig] = useState({});
    const [propList, setPropList] = useState([]);
    const [property, setProperty] = useState();
    const [activePropertyName, setActivePropertyName] = useState('');
    const [variables, setVariables] = useState([]);
    const [groups, setGroups] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [addModalOpen, setAddModalOpen] = useState(false);
    const [editingVariable, setEditingVariable] = useState(null);
    const keyManuallyEdited = useRef(false);
    const [form] = Form.useForm();
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();
    const { role, properties } = user || {};
    const { companyName, images } = propertyConfig;
    const { logoURL } = images || {};

    useEffect(() => {
        if (user && !isSuperAdmin(role)) {
            const storedProperty = localStorage.getItem('selectedProperty');
            const initialProperty = storedProperty || properties?.[0];
            if (initialProperty) {
                setProperty(initialProperty);
                setActivePropertyName(initialProperty);
                localStorage.setItem('selectedProperty', initialProperty);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (isSuperAdmin(role)) {
            getPropertyList().then((data) => {
                setPropList(data);
                const saved = localStorage.getItem('selectedProperty');
                if (saved) {
                    const found = data.find(p => p.propertyID === saved);
                    if (found) {
                        setProperty(saved);
                        setActivePropertyName(found.companyName);
                    }
                }
            });
        }
    }, [role]);

    useEffect(() => {
        if (user) return;
        navigate('/login');
    }, [user, navigate]);

    const fetchData = useCallback(async () => {
        if (!property) return;
        setFetchLoading(true);
        try {
            const [config, vars, grps] = await Promise.all([
                getPropertyConfig(property),
                getCostVariables(property),
                getVariableGroups(property),
            ]);
            setPropertyConfig(config || {});
            setVariables(vars || []);
            setGroups(grps || []);
            if (grps?.length > 0 && !selectedCategory) {
                setSelectedCategory(grps[0].category);
            }
        } catch (err) {
            enqueueSnackbar(t('home.error.loading'), { variant: 'error' });
            Sentry.captureException(err);
        } finally {
            setFetchLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [property, enqueueSnackbar]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const chooseProperty = (propertyID) => {
        setProperty(propertyID);
        setSelectedCategory(null);
        const found = propList.find(p => p.propertyID === propertyID);
        if (found) setActivePropertyName(found.companyName);
        localStorage.setItem('selectedProperty', propertyID);
    };

    const signOut = async () => {
        try { await logoutUser(); } catch { /* ignore */ }
        localStorage.removeItem('userDetails');
        localStorage.removeItem('selectedProperty');
        localStorage.removeItem('selectedPropertyName');
        navigate('/login');
    };

    const confirmIfDirty = (action) => action();

    const handleInlineValueChange = async (variable, newValue) => {
        if (newValue === undefined || newValue === null) return;

        const originalValue = variable.value;

        try {
            await executeOptimistic(
                variable._id,
                // Update function (executed optimistically)
                () => {
                    setVariables(prev =>
                        prev.map(v => v._id === variable._id ? { ...v, value: newValue } : v)
                    );
                },
                // Revert function (called on error)
                () => {
                    setVariables(prev =>
                        prev.map(v => v._id === variable._id ? { ...v, value: originalValue } : v)
                    );
                },
                // API call
                () => updateCostVariable(
                    variable._id,
                    variable.name,
                    variable.description,
                    variable.type,
                    newValue,
                    variable.formula,
                    variable.category,
                    variable.displayUnit,
                ),
                t('home.success.updated'),
                t('home.error.updating')
            );
        } catch (err) {
            Sentry.captureException(err);
        }
    };

    const openAddModal = () => {
        form.setFieldsValue({
            name: '',
            description: '',
            type: undefined,
            value: undefined,
            formula: '',
            category: undefined,
            key: '',
            displayUnit: getCurrencySymbol(propertyConfig?.currency || 'GBP')
        });
        keyManuallyEdited.current = false;
        setEditingVariable(null);
        setAddModalOpen(true);
    };

    const openEditModal = (variable) => {
        form.setFieldsValue({
            name: variable.name,
            description: variable.description,
            type: variable.type,
            value: variable.value,
            formula: variable.formula,
            category: variable.category,
            key: variable.key,
            displayUnit: variable.displayUnit,
        });
        setEditingVariable(variable);
        setAddModalOpen(true);
    };

    const handleModalSubmit = async (values) => {
        setLoading(true);
        try {
            if (editingVariable) {
                await updateCostVariable(
                    editingVariable._id,
                    values.name,
                    values.description,
                    values.type,
                    values.value,
                    values.formula,
                    values.category,
                    values.displayUnit,
                );
                enqueueSnackbar(t('home.success.updated'), { variant: 'success' });
            } else {
                await addCostVariable(
                    property,
                    values.name,
                    values.description,
                    values.type,
                    values.value,
                    values.formula,
                    values.category,
                    values.key,
                    values.displayUnit,
                );
                enqueueSnackbar(t('home.success.created'), { variant: 'success' });
            }
            setAddModalOpen(false);
            setEditingVariable(null);
            form.resetFields();
            await fetchData();
        } catch (err) {
            const errorMsg = err.response?.data || t('home.error.saving');
            enqueueSnackbar(errorMsg, { variant: 'error' });
            Sentry.captureException(err);
        } finally {
            setLoading(false);
        }
    };

    const getTypeColor = (type) => {
        switch (type) {
            case 'input': return 'blue';
            case 'fixed': return 'green';
            case 'calculated': return 'orange';
            default: return 'default';
        }
    };

    const filteredVariables = variables.filter(
        v => !selectedCategory || v.category === selectedCategory
    );

    const columns = [
        {
            title: t('home.columns.name'),
            dataIndex: 'name',
            key: 'name',
            width: '22%',
            sorter: (a, b) => (a?.name || '').localeCompare(b?.name || ''),
        },
        {
            title: t('home.columns.type'),
            dataIndex: 'type',
            key: 'type',
            width: '10%',
            render: (type) => (
                <Tag color={getTypeColor(type)}>
                    {formatTypeLabel(type)}
                </Tag>
            ),
        },
        {
            title: t('home.columns.key'),
            dataIndex: 'key',
            key: 'key',
            width: '18%',
            render: (key) => <code>{key}</code>,
        },
        {
            title: t('home.columns.value'),
            dataIndex: 'value',
            key: 'value',
            width: '22%',
            render: (value, record) => {
                if (record.type === 'calculated') {
                    return (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            Formula: <code>{record.formula}</code>
                        </Text>
                    );
                }
                return (
                    <Space>
                        <InputNumber
                            size="small"
                            value={value}
                            min={0}
                            precision={record.key?.toLowerCase().includes('perwatt') ? 10 : 2}
                            addonBefore={record.displayUnit || getCurrencySymbol(propertyConfig?.currency || 'GBP')}
                            style={{ width: 160 }}
                            onBlur={(e) => {
                                const parsed = parseFloat(e.target.value.replace(/[^0-9.-]/g, ''));
                                if (!isNaN(parsed) && parsed !== value) {
                                    handleInlineValueChange(record, parsed);
                                }
                            }}
                            onPressEnter={(e) => {
                                const parsed = parseFloat(e.target.value.replace(/[^0-9.-]/g, ''));
                                if (!isNaN(parsed) && parsed !== value) {
                                    handleInlineValueChange(record, parsed);
                                }
                            }}
                        />
                        {savingIds.has(record._id) && (
                            <Spin size="small" />
                        )}
                    </Space>
                );
            },
        },
        {
            title: t('home.columns.description'),
            dataIndex: 'description',
            key: 'description',
            ellipsis: true,
        },
        {
            title: '',
            key: 'actions',
            width: '8%',
            render: (_, record) => (
                <Button
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => openEditModal(record)}
                />
            ),
        },
    ];

    const categoryOptions = [
        ...groups.map(g => ({ value: g.category, label: g.name })),
        ...variables
            .map(v => v.category)
            .filter((cat, i, arr) => cat && arr.indexOf(cat) === i && !groups.some(g => g.category === cat))
            .map(cat => ({ value: cat, label: cat })),
    ];

    const categoryTabs = groups.map(g => ({ label: g.name, key: g.category }));

    const hasNoGroups = !fetchLoading && groups.length === 0;

    return (
        <PageLayout
            companyName={companyName}
            chooseProperty={chooseProperty}
            propList={propList}
            activePropertyName={activePropertyName}
            property={property}
            role={role}
            signOut={signOut}
            confirmIfDirty={confirmIfDirty}
        >
            <div style={{ maxWidth: 1100, margin: '0 auto' }}>
                {logoURL && (
                    <div style={{ textAlign: 'center', marginBottom: 24 }}>
                        <Image src={logoURL} alt="Company Logo" preview={false} style={{ maxHeight: 80 }} />
                    </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <Title level={2} style={{ margin: 0 }}>{t('home.title')}</Title>
                    <Space>
                        <Button
                            icon={<LinkOutlined />}
                            onClick={() => navigate(`/variables/${property}`)}
                            disabled={!property}
                        >
                            {t('home.fullManager')}
                        </Button>
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={openAddModal}
                            disabled={!property || hasNoGroups}
                        >
                            {t('home.addVariable')}
                        </Button>
                    </Space>
                </div>
                <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
                    {t('home.subtitle')}
                </Text>

                <Spin spinning={fetchLoading}>
                    {!property ? (
                        <Card>
                            <Empty description={t('home.selectProperty')} />
                        </Card>
                    ) : hasNoGroups ? (
                        <Card>
                            <Empty
                                image={<ToolOutlined style={{ fontSize: 48, color: '#bfbfbf' }} />}
                                description={
                                    <span>
                                        {t('home.noGroups')}{' '}
                                        <Button
                                            type="link"
                                            style={{ padding: 0 }}
                                            onClick={() => navigate(`/variables/${property}`)}
                                        >
                                            {t('home.goToManager')}
                                        </Button>{' '}
                                        {t('home.toCreateFirst')}
                                    </span>
                                }
                                style={{ margin: '32px 0' }}
                            />
                        </Card>
                    ) : variables.length === 0 ? (
                        <Card>
                            <Empty
                                description={t('home.noVariables')}
                                style={{ margin: '32px 0' }}
                            >
                                <Button type="primary" icon={<PlusOutlined />} onClick={openAddModal}>
                                    {t('home.addFirstVariable')}
                                </Button>
                            </Empty>
                        </Card>
                    ) : (
                        <Card>
                            <Tabs
                                items={categoryTabs}
                                activeKey={selectedCategory}
                                onChange={setSelectedCategory}
                                style={{ marginBottom: 16 }}
                            />
                            <Table
                                columns={columns}
                                dataSource={filteredVariables}
                                rowKey="_id"
                                pagination={{ pageSize: 15 }}
                                size="small"
                                scroll={{ x: 'max-content' }}
                            />
                        </Card>
                    )}
                </Spin>
            </div>

            {/* Add / Edit Variable Modal */}
            <Modal
                title={editingVariable ? t('home.editModal') : t('home.addModal')}
                open={addModalOpen}
                onCancel={() => { setAddModalOpen(false); setEditingVariable(null); form.resetFields(); }}
                footer={null}
                width={560}
                destroyOnClose
            >
                <Form form={form} onFinish={handleModalSubmit} layout="vertical" style={{ marginTop: 16 }}>
                    <Form.Item
                        label={t('variables.form.name')}
                        name="name"
                        rules={[{ required: true, message: t('variables.validation.nameRequired') }]}
                    >
                        <Input
                            placeholder={t('variables.form.namePlaceholder')}
                            maxLength={100}
                            onChange={e => {
                                if (!editingVariable && !keyManuallyEdited.current) {
                                    form.setFieldValue('key', toCamelCase(e.target.value));
                                }
                            }}
                        />
                    </Form.Item>

                    <Form.Item
                        label={t('variables.form.key')}
                        name="key"
                        rules={[
                            { required: true, message: t('variables.validation.keyRequired') },
                            { pattern: /^[a-zA-Z0-9_]+$/, message: t('variables.validation.keyInvalid') },
                            {
                                validator: (_, value) => {
                                    if (!value || editingVariable) return Promise.resolve();
                                    return variables.some(v => v.key === value)
                                        ? Promise.reject(new Error(t('variables.validation.keyDuplicate')))
                                        : Promise.resolve();
                                }
                            }
                        ]}
                    >
                        <Input
                            placeholder={t('variables.form.keyPlaceholder')}
                            disabled={!!editingVariable}
                            maxLength={100}
                            onChange={() => { keyManuallyEdited.current = true; }}
                        />
                    </Form.Item>

                    <Form.Item label={t('variables.form.description')} name="description">
                        <Input.TextArea placeholder={t('variables.form.descriptionPlaceholder')} rows={2} maxLength={500} />
                    </Form.Item>

                    <Form.Item
                        label={t('variables.form.type')}
                        name="type"
                        rules={[{ required: true, message: t('variables.validation.typeRequired') }]}
                    >
                        <Select>
                            <Select.Option value="input">{t('variables.form.typeInput')}</Select.Option>
                            <Select.Option value="fixed">{t('variables.form.typeFixed')}</Select.Option>
                            <Select.Option value="calculated">{t('variables.form.typeCalculated')}</Select.Option>
                        </Select>
                    </Form.Item>

                    <Form.Item noStyle shouldUpdate={(prev, curr) => prev.type !== curr.type}>
                        {({ getFieldValue }) =>
                            getFieldValue('type') !== 'calculated' ? (
                                <Form.Item
                                    label={t('variables.form.value')}
                                    name="value"
                                    rules={[{ required: true, message: t('variables.validation.valueRequired') }]}
                                >
                                    <InputNumber min={0} precision={10} placeholder="0.00" style={{ width: '100%' }} />
                                </Form.Item>
                            ) : null
                        }
                    </Form.Item>

                    <Form.Item noStyle shouldUpdate={(prev, curr) => prev.type !== curr.type}>
                        {({ getFieldValue }) =>
                            getFieldValue('type') === 'calculated' ? (
                                <Form.Item
                                    label={t('variables.form.formula')}
                                    name="formula"
                                    rules={[{ required: true, message: t('variables.validation.formulaRequired') }]}
                                >
                                    <Input.TextArea placeholder={t('variables.form.formulaPlaceholder')} rows={2} maxLength={1000} />
                                </Form.Item>
                            ) : null
                        }
                    </Form.Item>

                    <Form.Item
                        label={t('variables.form.category')}
                        name="category"
                        rules={[{ required: true, message: t('variables.validation.categoryRequired') }]}
                    >
                        <AutoComplete
                            placeholder={t('variables.form.categoryPlaceholder')}
                            options={categoryOptions}
                            filterOption={(input, option) =>
                                (option.label || option.value).toLowerCase().includes(input.toLowerCase())
                            }
                        />
                    </Form.Item>

                    <Form.Item
                        label="Display Unit"
                        name="displayUnit"
                    >
                        <Input
                            maxLength={3}
                            style={{ width: 80 }}
                        />
                    </Form.Item>

                    <Form.Item style={{ marginBottom: 0 }}>
                        <Space>
                            <Button type="primary" htmlType="submit" loading={loading}>
                                {editingVariable ? t('variables.updateButton') : t('variables.createButton')}
                            </Button>
                            <Button onClick={() => { setAddModalOpen(false); setEditingVariable(null); form.resetFields(); }}>
                                {t('common.cancel')}
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </PageLayout>
    );
}

export default Home;
